import { useMemo } from 'react';
import {
  CategoryType,
  getCategoryType,
  isProductCategory,
  isServiceCategory,
  isSystemCategory,
} from '@shared/helpers/element-category.helpers';
import {
  ElementCategoryID,
  IElementCategory,
} from '@shared/models/element-categories.model';
import { getAvailableCategories } from '@shared/templates/categories';
import { IElement } from '@shared/models/project.model';
import { useElementPath } from '@client/hooks/use-elements.hooks';
import { isMaintenanceElement } from '@shared/helpers/element.helpers';
import { ProjectValidationErrors } from '@shared/validation/project-validation';
import { useIsSelectedOrganizationWhitelisted } from '@client/store/organization/organization.hook';
import { guards } from '@shared/helpers/typeguards.helpers';

type CategoryTypeToLabel = {
  [CategoryType.System]: 'System';
  [CategoryType.Product]: 'Product';
  [CategoryType.Service]: 'Service';
  [CategoryType.Other]: '';
};

export type ICategoryGroup<
  CategoryMeta extends Record<string, unknown> | undefined,
> = {
  id: CategoryType;
  name: CategoryTypeToLabel[CategoryType];
  categories: (IElementCategory & { meta: CategoryMeta })[];
};

export const useGroupedElementCategories = (
  element?: IElement,
  includeCategories: Readonly<ElementCategoryID[]> = [],
): ICategoryGroup<{ disabled: string | false; type: CategoryType }>[] => {
  const elementPath = useElementPath(element);
  const isWhitelisted = useIsSelectedOrganizationWhitelisted();

  const categories = useMemo(
    () =>
      getAvailableCategories(element?.category_id)
        .filter(isWhitelisted)
        .filter(
          (category) =>
            includeCategories.length === 0 ||
            includeCategories.includes(category.id),
        ),
    [element?.category_id, includeCategories, isWhitelisted],
  );

  const groups = useMemo(
    () =>
      getCategoryGroups(categories, (category) => {
        return {
          disabled:
            category.id === ElementCategoryID.Maintenance &&
            elementPath.some(isMaintenanceElement) &&
            ProjectValidationErrors.ANCESTOR_HAS_MAINTENANCE_CATEGORY,
          type: getCategoryType(category.id),
        };
      }),
    [categories, elementPath],
  );

  return groups;
};

/**
 * Groups the categories into system, product, service and other.
 * @param categories
 * @returns
 */
const getCategoryGroups = <CategoryMeta extends Record<string, unknown>>(
  categories: IElementCategory[],
  getCategoryMeta: (category: IElementCategory) => CategoryMeta,
): ICategoryGroup<CategoryMeta>[] => {
  const extendedCategories = categories.map((category) => ({
    ...category,
    meta: getCategoryMeta?.(category),
  }));
  const system: ICategoryGroup<CategoryMeta> = {
    id: CategoryType.System,
    name: 'System',
    categories: [],
  };
  const product: ICategoryGroup<CategoryMeta> = {
    id: CategoryType.Product,
    name: 'Product',
    categories: [],
  };
  const service: ICategoryGroup<CategoryMeta> = {
    id: CategoryType.Service,
    name: 'Service',
    categories: [],
  };
  const other: ICategoryGroup<CategoryMeta> = {
    id: CategoryType.Other,
    name: '',
    categories: [],
  };
  extendedCategories.forEach((category) => {
    if (isSystemCategory(category.id)) system.categories.push(category);
    else if (isProductCategory(category.id)) product.categories.push(category);
    else if (isServiceCategory(category.id)) service.categories.push(category);
    else other.categories.push(category);
  });

  return [system, product, service, other].filter((group) =>
    guards.is.nonEmptyArray(group.categories),
  );
};
